// services/stock.js — сметките на склада (ПРОМТ-8). Чисти функции върху масиви,
// БЕЗ достъп до базата: route-овете четат движенията, викат тези функции и
// записват резултата в транзакция.
//
// Шестте правила на склада (архитектурата е замразена):
//  1. Регистърът stock_movements е САМО ЗА ДОБАВЯНЕ. Редакция = сторно + ново,
//     триене = сторно. Историята е самият регистър.
//  2. Количеството е винаги ПОЛОЖИТЕЛНО; знакът идва от типа (SIGN).
//  3. Всичко е в БАЗОВАТА мерна единица на артикула — преобразуването е при
//     записа, само през services/articles.js: toBase().
//  4. Връщането сочи оригинала (ref_movement_id) и взема неговата цена.
//  5. Периодът се заключва от експорта към Конто (companies.stock_lock_date);
//     тук само се проверява (checkDate).
//  6. Себестойността е СРЕДНОПРЕТЕГЛЕНА след всяко постъпление и се смята САМО
//     тук (recalc). Не пиши втора формула другаде.

const { round2 } = require('./totals');

function round4(n) {
  const v = Number(n) || 0;
  const r = Math.round((Math.abs(v) + Number.EPSILON) * 10000) / 10000;
  return v < 0 ? (r === 0 ? 0 : -r) : r;
}

const IN_TYPES = ['opening', 'receipt', 'return_in', 'transfer_in', 'adjustment_in'];
const OUT_TYPES = ['issue', 'return_out', 'transfer_out', 'adjustment_out'];
const TYPES = IN_TYPES.concat(OUT_TYPES, ['reversal']);
const SIGN = {};
IN_TYPES.forEach((t) => { SIGN[t] = 1; });
OUT_TYPES.forEach((t) => { SIGN[t] = -1; });

// Постъпления с цена, дадена при записа (документ, начално салдо, ръчна
// корекция). Всички останали цени са ПРОИЗВОДНИ и се преизчисляват.
const FIXED_COST = ['opening', 'receipt', 'transfer_in', 'adjustment_in'];

const SOURCE_TYPES = ['invoice', 'purchase_doc', 'oss_sale', 'pos_sale', 'count', 'manual'];

// Кои типове артикули се водят на склад според функцията на склада на фирмата.
function stockTypes(mode) {
  if (mode === 'trade') return ['goods'];
  if (mode === 'materials') return ['goods', 'material', 'dish'];
  return [];
}

function isStockArticle(article, mode) {
  return !!article && stockTypes(mode).includes(article.type);
}

// Изписва ли фактурата от CRM стоката от склада (ПРОМТ-16). ЕДИНСТВЕНОТО
// място за решението — ползват го сървърът (stockLink.planInvoice) и
// формата на фактурата. При ресторант продаденото идва от таблицата на
// касата; фактура към касов бон би го изписала втори път. Затова:
// изрично зададеното companies.invoice_stock печели, а без него — по
// функцията на склада (търговия → да, материали → не). Без миграция:
// заварените фирми получават правилното поведение сами.
function invoiceMovesStock(company) {
  if (!company) return false;
  const mode = company.warehouse_mode;
  if (mode !== 'trade' && mode !== 'materials') return false;
  if (company.invoice_stock === true || company.invoice_stock === false) return company.invoice_stock;
  return mode === 'trade';
}

function byIdMap(movements) {
  const m = new Map();
  for (const x of (Array.isArray(movements) ? movements : [])) if (x && x.id != null) m.set(Number(x.id), x);
  return m;
}

// Знакът на сторното е обратният на сторнираното движение — затова е нужен
// регистърът (byId). Без него сторно не може да се тълкува и това е грешка.
function signOf(m, byId) {
  if (!m) return 0;
  if (m.type === 'reversal') {
    const orig = byId && byId.get(Number(m.reversal_of));
    if (!orig) throw new Error('Сторно № ' + m.id + ' сочи несъществуващо движение.');
    return -signOf(orig, byId);
  }
  const s = SIGN[m.type];
  if (!s) throw new Error('Непознат тип складово движение „' + m.type + '".');
  return s;
}

function signedQty(m, byId) {
  return signOf(m, byId) * (Number(m.qty) || 0);
}

// Проверка на количество за запис — правило 2.
function qtyError(qty) {
  const q = Number(qty);
  if (!Number.isFinite(q) || q <= 0) return 'Количеството трябва да е положително число (посоката идва от вида на движението).';
  return null;
}

// Нова средна след постъпление. При нулева или отрицателна наличност старата
// средна няма тежест — новата е цената на постъплението.
function applyReceipt(avg, qtyOld, qtyIn, costIn) {
  const qo = Number(qtyOld) || 0;
  const qi = Number(qtyIn) || 0;
  if (qo <= 0 || qo + qi <= 0) return round4(costIn);
  return round4(((Number(avg) || 0) * qo + qi * (Number(costIn) || 0)) / (qo + qi));
}

function sortMovements(list) {
  return list.slice().sort((a, b) => {
    const d = String(a.date || '').localeCompare(String(b.date || ''));
    return d !== 0 ? d : (Number(a.id) || 0) - (Number(b.id) || 0);
  });
}

function sameKey(m, articleId, warehouseId) {
  if (Number(m.article_id) !== Number(articleId)) return false;
  if (warehouseId != null && Number(m.warehouse_id) !== Number(warehouseId)) return false;
  return true;
}

// Прекарва движенията на един артикул в един склад по ред (дата, id) и дава
// цената на всяко. ЕДИНСТВЕНОТО място, където се смята себестойност.
//
// Изходящо движение взема текущата средна; при наличност ≤ 0 — последната
// известна средна, после last_purchase_price на артикула, иначе остава
// ЧАКАЩО (cost_pending) и следващото постъпление го затваря със своята цена.
// Сторно взема цената на сторнираното; връщане — цената на оригинала.
function simulate(movements, articleId, warehouseId, lastPurchasePrice) {
  const all = Array.isArray(movements) ? movements : [];
  const byId = byIdMap(all);
  const own = sortMovements(all.filter((m) => sameKey(m, articleId, warehouseId)));
  const lpp = Number(lastPurchasePrice) > 0 ? Number(lastPurchasePrice) : null;
  const cost = new Map(); // id → { unit_cost, total_cost, cost_pending }
  let qty = 0;
  let value = 0;
  let lastAvg = null;
  let pending = []; // чакащи изходящи, които следващото постъпление затваря

  const avgNow = () => (qty > 0 ? round4(value / qty) : lastAvg);
  const costOfRef = (refId) => {
    const c = cost.get(Number(refId));
    if (c) return c;
    const r = byId.get(Number(refId));
    return r ? { unit_cost: Number(r.unit_cost) || 0, total_cost: Number(r.total_cost) || 0, cost_pending: !!r.cost_pending } : null;
  };

  function incoming(m, unitCost) {
    const q = Number(m.qty) || 0;
    const c = round4(unitCost);
    // Затваряне на чакащите: цената им става цената на постъплението, а
    // стойността на склада се коригира с изписаното по нея.
    if (pending.length) {
      for (const p of pending) {
        const t = round2((Number(p.qty) || 0) * c);
        cost.set(Number(p.id), { unit_cost: c, total_cost: t, cost_pending: false });
        value = round2(value - t);
      }
      pending = [];
    }
    const t = round2(q * c);
    cost.set(Number(m.id), { unit_cost: c, total_cost: t, cost_pending: false });
    qty = round4(qty + q);
    value = round2(value + t);
    if (qty > 0) lastAvg = round4(value / qty);
  }

  function outgoing(m, unitCost, pendingFlag) {
    const q = Number(m.qty) || 0;
    if (pendingFlag) {
      cost.set(Number(m.id), { unit_cost: 0, total_cost: 0, cost_pending: true });
      pending.push(m);
      qty = round4(qty - q);
      return;
    }
    const c = round4(unitCost);
    // Изписва ли се ЦЯЛАТА наличност — взима цялата стойност, за да не остават
    // стотинки от закръгляния при нулево количество.
    const t = (qty > 0 && Math.abs(q - qty) < 1e-9) ? value : round2(q * c);
    cost.set(Number(m.id), { unit_cost: c, total_cost: t, cost_pending: false });
    qty = round4(qty - q);
    value = round2(value - t);
    if (qty > 0) lastAvg = round4(value / qty);
  }

  for (const m of own) {
    const type = m.type;
    if (FIXED_COST.includes(type)) {
      incoming(m, Number(m.unit_cost) || 0);
    } else if (type === 'return_in') {
      // Клиентът връща — по цената на изписването, което сочи (правило 4).
      const ref = m.ref_movement_id != null ? costOfRef(m.ref_movement_id) : null;
      if (ref && !ref.cost_pending) incoming(m, ref.unit_cost);
      else {
        const a = avgNow();
        if (a != null) incoming(m, a);
        else incoming(m, lpp || 0);
      }
    } else if (type === 'return_out') {
      // Връщане към доставчика — по цената на доставката, която сочи.
      const ref = m.ref_movement_id != null ? costOfRef(m.ref_movement_id) : null;
      if (ref) outgoing(m, ref.unit_cost, false);
      else {
        const a = avgNow();
        if (a != null) outgoing(m, a, false);
        else if (lpp != null) outgoing(m, lpp, false);
        else outgoing(m, 0, true);
      }
    } else if (type === 'reversal') {
      const orig = byId.get(Number(m.reversal_of));
      const oc = orig ? costOfRef(orig.id) : null;
      const s = signOf(m, byId);
      if (oc && oc.cost_pending) {
        // Сторно на чакащо: двете се неутрализират — чакащото вече не чака.
        pending = pending.filter((p) => Number(p.id) !== Number(orig.id));
        cost.set(Number(orig.id), { unit_cost: 0, total_cost: 0, cost_pending: false });
        cost.set(Number(m.id), { unit_cost: 0, total_cost: 0, cost_pending: false });
        qty = round4(qty + s * (Number(m.qty) || 0));
      } else if (s > 0) {
        // Сторно на изходящо: стоката се връща по цената, по която е излязла.
        incoming(m, oc ? oc.unit_cost : 0);
      } else {
        // Сторно на постъпление: излиза ТОЧНО стойността, която е влязла.
        const q = Number(m.qty) || 0;
        const t = oc ? oc.total_cost : 0;
        cost.set(Number(m.id), { unit_cost: oc ? oc.unit_cost : 0, total_cost: t, cost_pending: false });
        qty = round4(qty - q);
        value = round2(value - t);
        if (qty > 0) lastAvg = round4(value / qty);
      }
    } else if (OUT_TYPES.includes(type)) {
      const a = qty > 0 ? avgNow() : lastAvg;
      if (a != null) outgoing(m, a, false);
      else if (lpp != null) outgoing(m, lpp, false);
      else outgoing(m, 0, true);
    } else {
      throw new Error('Непознат тип складово движение „' + type + '".');
    }
  }
  return { cost, qty, value, avg: avgNow() };
}

// Преизчислява цените от fromDate нататък. Връща САМО промените
// { id, unit_cost, total_cost, cost_pending } — route-ът ги записва в
// транзакция. Това е единственото изключение от „движение не се променя":
// себестойността е ПРОИЗВОДНА величина (задна дата, затворено чакащо).
// Количеството, типът и датата не се пипат никога.
function recalc(movements, articleId, warehouseId, fromDate, lastPurchasePrice) {
  const sim = simulate(movements, articleId, warehouseId, lastPurchasePrice);
  const from = String(fromDate || '');
  const out = [];
  for (const m of (Array.isArray(movements) ? movements : [])) {
    if (!sameKey(m, articleId, warehouseId)) continue;
    const c = sim.cost.get(Number(m.id));
    if (!c) continue;
    // Чакащото преди fromDate може да се затвори от постъпление след него —
    // затова чакащите се гледат винаги, не само от датата нататък.
    if (String(m.date || '') < from && !m.cost_pending) continue;
    const same = round4(m.unit_cost) === c.unit_cost && round2(m.total_cost) === c.total_cost && !!m.cost_pending === c.cost_pending;
    if (!same) out.push({ id: Number(m.id), unit_cost: c.unit_cost, total_cost: c.total_cost, cost_pending: c.cost_pending });
  }
  return out;
}

// Наличности по артикул: Map(articleId → { qty, avg, value }). По записаните
// цени (recalc вече ги е изравнил). warehouseId — само един склад; asOf —
// включително тази дата.
function balances(movements, opts) {
  const o = opts || {};
  const all = Array.isArray(movements) ? movements : [];
  const byId = byIdMap(all);
  const out = new Map();
  for (const m of all) {
    if (o.warehouseId != null && Number(m.warehouse_id) !== Number(o.warehouseId)) continue;
    if (o.asOf && String(m.date || '') > String(o.asOf)) continue;
    const s = signOf(m, byId);
    const key = Number(m.article_id);
    const b = out.get(key) || { qty: 0, value: 0, avg: 0 };
    b.qty = round4(b.qty + s * (Number(m.qty) || 0));
    b.value = round2(b.value + s * (Number(m.total_cost) || 0));
    out.set(key, b);
  }
  for (const b of out.values()) b.avg = b.qty > 0 ? round4(b.value / b.qty) : 0;
  return out;
}

// Оборотна ведомост за [from, to]: по артикул начално, приход, разход, крайно
// (количество и стойност). Сторното се показва в колоната на сторнираното с
// обратен знак — сторно на изписване намалява разхода, не е „приход".
function turnover(movements, from, to, opts) {
  const o = opts || {};
  const all = Array.isArray(movements) ? movements : [];
  const byId = byIdMap(all);
  const rows = new Map();
  const row = (id) => {
    let r = rows.get(id);
    if (!r) {
      r = { article_id: id, opening_qty: 0, opening_value: 0, in_qty: 0, in_value: 0, out_qty: 0, out_value: 0, closing_qty: 0, closing_value: 0 };
      rows.set(id, r);
    }
    return r;
  };
  for (const m of all) {
    if (o.warehouseId != null && Number(m.warehouse_id) !== Number(o.warehouseId)) continue;
    const d = String(m.date || '');
    if (to && d > to) continue;
    const r = row(Number(m.article_id));
    const q = Number(m.qty) || 0;
    const v = Number(m.total_cost) || 0;
    const s = signOf(m, byId);
    if (from && d < from) {
      r.opening_qty = round4(r.opening_qty + s * q);
      r.opening_value = round2(r.opening_value + s * v);
      continue;
    }
    // Посоката на КОЛОНАТА: сторното отива при сторнираното, с минус.
    const base = m.type === 'reversal' ? byId.get(Number(m.reversal_of)) : m;
    const colIn = SIGN[base && base.type] > 0;
    const k = m.type === 'reversal' ? -1 : 1;
    if (colIn) { r.in_qty = round4(r.in_qty + k * q); r.in_value = round2(r.in_value + k * v); }
    else { r.out_qty = round4(r.out_qty + k * q); r.out_value = round2(r.out_value + k * v); }
  }
  for (const r of rows.values()) {
    r.closing_qty = round4(r.opening_qty + r.in_qty - r.out_qty);
    r.closing_value = round2(r.opening_value + r.in_value - r.out_value);
  }
  return [...rows.values()];
}

const MONTHS = ['Януари', 'Февруари', 'Март', 'Април', 'Май', 'Юни', 'Юли', 'Август', 'Септември', 'Октомври', 'Ноември', 'Декември'];

function dmy(iso) {
  const p = String(iso || '').slice(0, 10).split('-');
  return p.length === 3 ? p[2] + '.' + p[1] + '.' + p[0] : String(iso || '');
}

function nextDay(iso) {
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

// null = датата е свободна; иначе текстът на отказа (правило 5).
function checkDate(company, date) {
  const lock = String((company && company.stock_lock_date) || '');
  const d = String(date || '').slice(0, 10);
  if (!lock || !/^\d{4}-\d{2}-\d{2}$/.test(lock)) return null;
  if (!d || d > lock) return null;
  const after = nextDay(lock);
  // Заключено до края на месец → „Август 2026 е изнесен…"; иначе с дата.
  const period = after.slice(8, 10) === '01'
    ? MONTHS[Number(lock.slice(5, 7)) - 1] + ' ' + lock.slice(0, 4)
    : 'Периодът до ' + dmy(lock);
  return period + ' е изнесен към Конто — дата ≥ ' + dmy(after) + ' или отключване от админ.';
}

function articleLabel(a) {
  return (a && (a.code ? a.code + ' ' : '') + (a.name || '')) || 'артикул';
}

// Изписването за редове на документ. items = [{ article_id, qty (базова),
// source_line }] — вече преобразувани; articles = Map(id → артикул);
// bal = balances() на склада. Връща { movements, warnings, error }:
// при недостиг и negative_stock = 'block' → error (отказ ПРЕДИ записа),
// при 'warn' → warnings и движенията се записват.
function issueFor(items, articles, bal, company, opts) {
  const o = opts || {};
  const mode = (company && company.warehouse_mode) || 'none';
  const movements = [];
  const need = new Map();
  for (const it of (Array.isArray(items) ? items : [])) {
    const a = articles instanceof Map ? articles.get(Number(it.article_id)) : null;
    if (!isStockArticle(a, mode)) continue;
    const err = qtyError(it.qty);
    if (err) return { movements: [], warnings: [], error: err };
    movements.push({
      warehouse_id: o.warehouseId != null ? o.warehouseId : null,
      article_id: Number(it.article_id),
      date: o.date || '',
      // Изходящ тип: изписване по документ или ръчен разход/корекция.
      type: o.type || 'issue',
      qty: round4(it.qty),
      source_type: o.sourceType || 'invoice',
      source_id: o.sourceId != null ? o.sourceId : null,
      source_line: it.source_line != null ? it.source_line : null,
    });
    need.set(Number(it.article_id), round4((need.get(Number(it.article_id)) || 0) + Number(it.qty)));
  }
  const warnings = [];
  // shortages — кои артикули не стигат, като данни (не само текст): нужни са
  // напр. на пропусната повтаряща се фактура („недостиг на: …").
  const shortages = [];
  for (const [id, q] of need) {
    const have = (bal && bal.get(id) && bal.get(id).qty) || 0;
    if (round4(have - q) < 0) {
      const a = articles.get(id);
      warnings.push('Недостиг: ' + articleLabel(a) + ' — налични ' + have + ' ' + ((a && a.measure) || '') + ', изписват се ' + q + '.');
      shortages.push({ article_id: id, name: (a && a.name) || articleLabel(a), have, need: q });
    }
  }
  if (warnings.length && company && company.negative_stock === 'block') {
    return { movements: [], warnings: [], shortages, error: warnings.join(' ') + ' Фирмата не допуска отрицателни наличности.' };
  }
  return { movements, warnings, shortages, error: null };
}

module.exports = {
  SIGN, TYPES, IN_TYPES, OUT_TYPES, FIXED_COST, SOURCE_TYPES,
  round2, round4, stockTypes, isStockArticle, invoiceMovesStock, signedQty, signOf, qtyError,
  applyReceipt, sortMovements, simulate, recalc, balances, turnover, checkDate, issueFor,
};
